import moment from "moment";
import React, { useState, useEffect, useCallback } from "react";
import { useRef } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { importStudentFromExcel } from "../../api/student";
import { createExportRequest, getCompanyExportRequestStatus, logExportAction } from "../../api";
import Loader from "../../component/Loader";
import DeleteModal from "../../component/Modal/Delete";
import Paginations from "../../component/Pagination";
import ImportModal from "./ImportModal";
import { variants } from "../../constant/Variants";
import { areaCodeListingSelector, FetchAreaCodeList } from "../areacode/slice";
import { FetchDisciplineList } from "../discipline/DisciplineSlice";
import { SchoolListReducer, FetchCompanySettings } from "../slice";
import Select from "react-select";
import {
  FetchStudentList,
  studentListSelector,
  totalStudentCountSelector,
} from "../student/studentslice";

function ImportExportStudent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const arealist = useSelector(areaCodeListingSelector);
  const Schoollist = useSelector((state) => state.main.schools);
  const disciplineList = useSelector((state) => state.discipline.list);
  const companySettings = useSelector((state) => state.main.companySettings);
  const list = useSelector(studentListSelector);
  const total = useSelector(totalStudentCountSelector);
  const authState = useSelector((store) => store.auth);
  const userDetails = useSelector((store) => store.subAdmin.detail);

  const parseBooleanFlag = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.toLowerCase();
      if (["1", "true", "yes"].includes(normalized)) return true;
      if (["0", "false", "no"].includes(normalized)) return false;
    }
    return null;
  };

  const resolvedIsCompany = parseBooleanFlag(userDetails?.is_company);
  const fallbackIsCompany = (authState?.user_type || "")?.toLowerCase() === "company";
  const isCompanyAdmin = resolvedIsCompany ?? fallbackIsCompany;
  const isSuperAdmin = resolvedIsCompany !== null
    ? !resolvedIsCompany
    : (authState?.admin_type || "")?.toLowerCase() === "super admin";
  const canImport = isSuperAdmin;
  const canExport = isSuperAdmin || isCompanyAdmin;
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [modalName, setModalName] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [latestDisapprovedRequest, setLatestDisapprovedRequest] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const csvLink = useRef();
  let companyOptions = [];
  const companiesStr = localStorage.getItem('companies');
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = [
          { value: 0, label: 'All Companies' },
          ...companies.map(c => ({
            value: c.id,
            label: c.company_name
          }))
        ];
      }
    } catch (e) {
      // Optionally handle JSON parse error
      companyOptions = [];
    }
  }
  const [selectedCompany, setSelectedCompany] = useState(0);
  const [filters, setFilters] = useState({
    schoolId: "",
    areacode: "",
    discipline: "",
    membership_status: "",
    age: "",
  });

  const getVenues = useCallback(() => {
    dispatch(SchoolListReducer({ areacode: filters.areacode, company_id : selectedCompany }));
  }, [filters.areacode, dispatch, selectedCompany]);

  useEffect(() => {
    dispatch(FetchAreaCodeList({company_id : selectedCompany}));
    dispatch(FetchDisciplineList({company_id : selectedCompany}));
    dispatch(FetchCompanySettings({company_id : selectedCompany}));
    getVenues();
  }, [getVenues, dispatch, selectedCompany]);

  const handlePage = (val) => {
    setPage(val);
  };

  const handleShow = () => {
    setShow(!show);
  };

  const getList = useCallback(() => {
    setLoading(true);
    dispatch(
      FetchStudentList({
        page: page,
        limit: 10,
        search: search,
        discipline: filters?.discipline,
        schoolId: filters?.schoolId,
        areacode: filters?.areacode,
        trialAccountStatus: filters?.membership_status,
        // age: filters?.age,
        student_type: filters?.student_type,
        company_id : selectedCompany
      })
    ).then(() => {
      setLoading(false);
    });
  }, [
    dispatch,
    page,
    search,
    filters.discipline,
    filters.schoolId,
    filters.areacode,
    filters.membership_status,
    filters.student_type,
    selectedCompany
  ]);

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    if (isCompanyAdmin && userDetails?.id) {
      checkPendingRequest();
    }
  }, [isCompanyAdmin, userDetails?.id]);

  const checkPendingRequest = async () => {
    if (!userDetails?.id) return;
    
    const response = await getCompanyExportRequestStatus(userDetails.id, "student");
    if (response?.code === 1 && response?.data) {
      // Handle the new response structure
      if (response.data.hasPendingRequest) {
        setPendingRequest(response.data.pendingRequest);
      } else if (response.data.hasApprovedRequest) {
        setPendingRequest(response.data.approvedRequest);
      } else {
        // No active requests (pending or approved)
        setPendingRequest(null);
      }
      
      // Use latestRequest for disapproved message
      const latestRequest = response.data.latestRequest;
      if (latestRequest && latestRequest.status === 2) {
        setLatestDisapprovedRequest(latestRequest);
      } else {
        setLatestDisapprovedRequest(null);
      }
    } else {
      setPendingRequest(null);
      setLatestDisapprovedRequest(null);
    }
  };

  const handleDOwnloadCSV = async () => {
    if (isCompanyAdmin) {
      await handleCompanyExport();
    } else if (isSuperAdmin) {
      await handleSuperAdminExport();
    }
  };

  const handleCompanyExport = async () => {
    if (pendingRequest) {
      if (pendingRequest.status === 0) {
        toast.info("Your export request is pending for approval.");
        return;
      } else if (pendingRequest.status === 2) {
        toast.error("Your export request has been disapproved. Please submit a new request.");
        return;
      } else if (pendingRequest.status === 1) {
        // Request is approved, allow export
        await performExport();
        return;
      }
    }

    // No pending request, create new one
    if (!userDetails || !userDetails.id) {
      toast.error("User details not available. Please refresh the page.");
      return;
    }
    setLoadingRequest(true);
    const response = await createExportRequest(userDetails.id, 'student');
    setLoadingRequest(false);

    if (response?.code === 1) {
      toast.success("Your export request has been submitted for approval.");
      setPendingRequest(response.data);
      await checkPendingRequest();
    } else {
      toast.error(response?.message || "Failed to submit export request");
    }
  };

  const performExport = () => {
    setDownloading(true);
    dispatch(
      FetchStudentList({
        search: search,
        discipline: filters?.discipline,
        schoolId: filters?.schoolId,
        areacode: filters?.areacode,
        trialAccountStatus: filters?.membership_status,
        // age: filters?.age,
        student_type: filters?.student_type,
        company_id : selectedCompany
      })
    )
      .then((res) => {
        let list = res?.payload?.data?.student_list;
        if (list?.length > 0) {
          let arr = list?.map((item) => {
            let data = {
              user_email: item?.parent_details?.email,
              first_name: item?.first_name,
              last_name: item?.last_name,
              dob: item?.dob,
              dob_year: item?.dob_year,
              dob_month: item?.dob_month,
              dob_day: item?.dob_day,
              gender: item?.gender,
              student_type: item?.student_type,
              discipline: item?.discipline,
              emergency_countryCode: item?.emergency_countryCode,
              emergency_contactNo: item?.emergency_contactNo,
              medical_note: item?.medical_note,
              schoolId: item?.schoolId,
              areacode: item?.areacode,
              trialAccountStatus: item?.trialAccountStatus,
              membership_level: item?.membership_details?.membership_level,
              valid_till:
                moment(item?.membership_details?.valid_till).format(
                  "DD MMM YYYY"
                ) || "",
              membership_start_date: moment(
                item?.membership_details?.start_date
              ).format("DD MMM YYYY"),
            };
            return data;
          });

          setCsvData(arr);
          
          // Log the export action to backend
          if (arr.length > 0) {
            return logExportAction('student', arr.length, 'csv', selectedCompany).then((logRes) => {
              if (logRes?.code === 1) {
                console.log('Export logged successfully');
              } else {
                console.error('Failed to log export:', logRes?.message);
              }
              // Return the original response for the next then()
              return res;
            });
          }
          return res; // Return original response if no logging needed
        }
      })
      .then((res) => {
        setDownloading(false);
        if (res?.payload?.code === 1) {
          // Clear disapproved message after successful export
          setLatestDisapprovedRequest(null);
          // Refresh request status to get updated state
          if (isCompanyAdmin && userDetails?.id) {
            checkPendingRequest();
          }
          
          if (csvLink.current) {
            csvLink.current.link.click();
          } else {
            console.error('CSVLink ref is not available');
          }
        }
      });
  };

  const handleSuperAdminExport = () => {
    setDownloading(true);
    dispatch(
      FetchStudentList({
        search: search,
        discipline: filters?.discipline,
        schoolId: filters?.schoolId,
        areacode: filters?.areacode,
        trialAccountStatus: filters?.membership_status,
        // age: filters?.age,
        student_type: filters?.student_type,
        company_id : selectedCompany
      })
    )
      .then((res) => {
        let list = res?.payload?.data?.student_list;
        if (list?.length > 0) {
          let arr = list?.map((item) => {
            let data = {
              user_email: item?.parent_details?.email,
              first_name: item?.first_name,
              last_name: item?.last_name,
              dob: item?.dob,
              dob_year: item?.dob_year,
              dob_month: item?.dob_month,
              dob_day: item?.dob_day,
              gender: item?.gender,
              student_type: item?.student_type,
              discipline: item?.discipline,
              emergency_countryCode: item?.emergency_countryCode,
              emergency_contactNo: item?.emergency_contactNo,
              medical_note: item?.medical_note,
              schoolId: item?.schoolId,
              areacode: item?.areacode,
              trialAccountStatus: item?.trialAccountStatus,
              membership_level: item?.membership_details?.membership_level,
              valid_till:
                moment(item?.membership_details?.valid_till).format(
                  "DD MMM YYYY"
                ) || "",
              membership_start_date: moment(
                item?.membership_details?.start_date
              ).format("DD MMM YYYY"),
            };
            return data;
          });

          setCsvData(arr);
          
          // Log the export action to backend
          if (arr.length > 0) {
            return logExportAction('student', arr.length, 'csv', selectedCompany).then((logRes) => {
              if (logRes?.code === 1) {
                console.log('Export logged successfully');
              } else {
                console.error('Failed to log export:', logRes?.message);
              }
              // Return the original response for the next then()
              return res;
            });
          }
          return res; // Return original response if no logging needed
        }
      })
      .then((res) => {
        setDownloading(false);
        console.log('res', res);
        if (res?.payload?.code === 1) {
          if (csvLink.current) {
            csvLink.current.link.click();
          } else {
            console.error('CSVLink ref is not available');
          }
        }
      });
  };

  const handleImportStudent = (file, companyId) => {
    setImporting(true);
    importStudentFromExcel({ file, company_id: companyId }).then((res) => {
      if (res?.code === 1) {
        toast.success(res?.message || 'Students imported successfully');
        getList();
        setShowImportModal(false);
      } else {
        // Show detailed error information
        let errorMessage = res?.message || res?.msg || 'Import failed';
        
        // If there's additional error data, show it
        if (res?.data?.error) {
          errorMessage += `: ${res.data.error}`;
        }
        
        toast.error(errorMessage, { autoClose: false, closeButton: true });
        console.error('Import error details:', res);
      }
    }).catch((error) => {
      // This should rarely be called now since postFormData handles errors
      let errorMessage = 'Import failed';
      if (error?.message) {
        errorMessage += `: ${error.message}`;
      }
      toast.error(errorMessage, { autoClose: false, closeButton: true });
      console.error('Import error:', error);
    }).finally(() => {
      setImporting(false);
    })
  };

  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center col-rever">
          <div className="col-sm-12 col-md-6 col-lg-6 mb-3">
            <div className="link-active shadow">
              <p>
                <img src="assets/images/event-calender-selected.png" alt="" />{" "}
                {isCompanyAdmin ? "Data Export" : "Data Import / Export"} {total}
              </p>
            </div>
          </div>
          <div className="col-sm-12 col-md-6 col-lg-6 mb-3">
            <div className="d-flex align-items-center lg-justify-content-end">
              <form className="has-search mx-2">
                <span className="form-search-icon form-control-feedback"></span>
                <input
                  type="text"
                  className="form-control shadow"
                  placeholder="Search by name"
                  value={search}
                  onChange={(e) => {
                    setPage(1);

                    setSearch(e.target.value);
                  }}
                />
              </form>
              {canImport && (
                <button 
                  className="btn filter btn-accept px-3 fs-6 d-flex align-items-center"
                  onClick={() => setShowImportModal(true)}
                >
                  <img
                    src="assets/images/download.png"
                    alt=""
                    className="download-icon"
                  />{" "}
                  Import
                </button>
              )}

              <CSVLink
                data={csvData}
                asyncOnClick={true}
                filename={"student_list.csv"}
                //   onClick={(event, done) => {
                //     handleDOwnloadCSV(done);
                //   }}
                className="d-none"
                ref={csvLink}
              ></CSVLink>
              {canExport && (
                <>
                  <button
                    className="btn btn-accept btn-view px-3 fs-6 ms-0 fs-normal"
                    onClick={handleDOwnloadCSV}
                    disabled={loadingRequest || downloading || (isCompanyAdmin && pendingRequest && pendingRequest.status == 0)}
                  >
                    <img
                      src="assets/images/download.png"
                      alt=""
                      className="download-icon"
                    />
                    {loadingRequest ? "Submitting..." : 
                     downloading ? "Downloading..." :
                     pendingRequest?.status === 0 ? "Request Pending" :
                     (pendingRequest?.status === 1 || pendingRequest?.status === 2) ? "Export CSV" :
                     "Export CSV"}
                  </button>
                  {isCompanyAdmin && (pendingRequest || latestDisapprovedRequest) && (
                    <small className={`d-block mt-1 ${
                      pendingRequest?.status === 0 ? "text-warning" :
                      pendingRequest?.status === 1 ? "text-success" :
                      latestDisapprovedRequest ? "text-danger" :
                      "text-danger"
                    }`}>
                      {pendingRequest?.status === 0 && "Your export request is pending for approval."}
                      {pendingRequest?.status === 1 && "Your export request has been approved. You can now export data."}
                      {latestDisapprovedRequest && "Your export request has been disapproved. Please submit a new request."}
                    </small>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="row mt-1">
          <div className="col-sm-9 col-md-9 col-lg-10">
            <ul
              className="nav nav-pills group-nav mb-3"
              id="pills-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link "
                  id="pills-new-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-new"
                  type="button"
                  role="tab"
                  aria-controls="pills-new"
                  aria-selected="true"
                  onClick={() => {
                    navigate("/import-export-data/user");
                  }}
                >
                  Users
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link active"
                  id="pills-completed-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-completed"
                  type="button"
                  role="tab"
                  aria-controls="pills-completed"
                  aria-selected="false"
                  onClick={() => {
                    navigate("/import-export-data/student");
                  }}
                >
                  Students
                </button>
              </li>
              {isSuperAdmin && (
               <>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="pills-requests-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-requests"
                    type="button"
                    role="tab"
                    aria-controls="pills-requests"
                    aria-selected="false"
                    onClick={() => {
                      navigate("/import-export-data/requests");
                    }}
                  >
                    Export Requests
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="pills-logs-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-logs"
                    type="button"
                    role="tab"
                    aria-controls="pills-logs"
                    aria-selected="false"
                    onClick={() => {
                      navigate("/import-export-data/logs");
                    }}
                  >
                    Logs
                  </button>
                </li>
              </>
              )}
            </ul>
          </div>
          {Array.isArray(companyOptions) && companyOptions.length > 0 && (
          <div className="col-sm-12 col-md-6 col-lg-6 my-2">
          <Select
                options={companyOptions}
                isSearchable
                placeholder="Select a company..."
                defaultValue={companyOptions[selectedCompany]}
                onChange={option => {
                  setSelectedCompany(option.value);
                }}
              />
            </div>
          )}
          <div className="col-sm-12 col-md-12 col-lg-12 my-2">
            <div className="dropSchool">
              <select
                className="form-control form-select"
                onChange={(e) => {
                  setFilters((prev) => {
                    return {
                      ...prev,
                      areacode: e.target.value,
                    };
                  });
                }}
                value={filters?.areacode}
              >
                <option value="">Select Region</option>
                {Array.isArray(arealist) && arealist?.map((item, i) => (
                  <option value={item?.id} key={i}>
                    {item?.areacode}
                  </option>
                ))}
              </select>
              {filters?.areacode && (
                <select
                  className="form-control form-select"
                  onChange={(e) => {
                    setFilters((prev) => {
                      return {
                        ...prev,
                        schoolId: e.target.value,
                      };
                    });
                  }}
                  value={filters?.schooldId}
                >
                  <option value="">Select Location</option>
                  {Schoollist?.map((item, i) => (
                    <option value={item?.id} key={i}>
                      {item?.name}
                    </option>
                  ))}
                </select>
              )}

              <select
                className="form-control form-select"
                onChange={(e) => {
                  setFilters((prev) => {
                    return {
                      ...prev,
                      discipline: e.target.value,
                    };
                  });
                }}
                value={filters?.discipline}
              >
                <option value="">Select Activity Type</option>
                {disciplineList?.map((item, i) => (
                  <option value={item?.id} key={i}>
                    {item?.name}
                  </option>
                ))}
              </select>
              {companySettings?.membership && (
                <select
                  className="form-control form-select"
                  onChange={(e) => {
                    setFilters((prev) => {
                      return {
                        ...prev,
                        membership_status: e.target.value,
                      };
                    });
                  }}
                  value={filters?.membership_status}
                >
                  <option value="">Select Membership Status</option>
                  <option value="1">Trial</option>
                  <option value="2">Active</option>
                  <option value="3">Inactive</option>
                </select>
              )}
              <select
                className="form-control form-select"
                onChange={(e) => {
                  setPage(1);
                  setFilters((prev) => {
                    return {
                      ...prev,
                      student_type: e.target.value,
                    };
                  });
                }}
                value={filters?.student_type}
              >
                <option value="">Select Student Type</option>
                <option value="child">Child</option>
                <option value="adult">Adult</option>
              </select>
              {/* <input
                type="number"
                className="form-control"
                value={filters?.age}
                onChange={(e) => {
                  setFilters((prev) => {
                    return {
                      ...prev,
                      age: e.target.value,
                    };
                  });
                }}
                placeholder={"Search Student By Age"}
              /> */}
              <button
                type="button"
                className="btn btn-save p-2 fs-6"
                onClick={() => {
                  setFilters({
                    schoolId: "",
                    areacode: "",
                    discipline: "",
                    membership_status: "",
                    age: "",
                    student_type: "",
                  });
                }}
              >
                Reset
              </button>
            </div>
          </div>
          <div className="col-sm-12">
            <div className="table-responsive text-center">
              <table className="table storetable">
                <thead className="text-center">
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Parent Email</th>
                    <th scope="col">First Name</th>
                    <th scope="col">Last Name</th>
                    <th scope="col">Student Type</th>
                    <th scope="col">Discipline</th>
                    <th scope="col">Date of Birth</th>
                    <th scope="col">Emergency Contact Number</th>
                    <th scope="col">Venue</th>
                    {companySettings?.membership && (
                      <th scope="col">Membership Status</th>
                    )}

                    {/* <th scope="col">action</th> */}
                  </tr>
                  <tr className="border-0 bg-none invisible">
                    <td className="border-0 bg-none"></td>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <td colSpan={10}>
                      <Loader />
                    </td>
                  ) : (
                    <>
                      {list?.length > 0 ? (
                        list?.map((item, i) => {
                          return (
                            <tr variants={variants.card} key={i}>
                              <td>{item?.id || "NA"}</td>
                              <td>{item?.parent_details?.email || "NA"}</td>
                              <td>
                                {item?.first_name ? item?.first_name : "NA"}
                              </td>
                              <td>
                                {item?.last_name ? item?.last_name : "NA"}
                              </td>
                              <td>
                                {item?.student_type ? item?.student_type : "NA"}
                              </td>
                              <td>
                                {item?.discipline_details
                                  ? item?.discipline_details?.name
                                  : "NA"}
                              </td>
                              <td>
                                {item?.dob && item?.dob !== "0000-00-00"
                                  ? moment(item?.dob).format("DD MMM YYYY")
                                  : item?.dob_year
                                  ? moment(
                                      `${item?.dob_year}-${item?.dob_month}-${item?.dob_day}`
                                    ).format("DD MMM YYYY")
                                  : "NA"}
                              </td>
                              <td>
                                {item?.emergency_contactNo
                                  ? `${item?.emergency_countryCode}${item?.emergency_contactNo}`
                                  : "NA"}
                              </td>
                              {/* <td>
                            {item?.address_details
                              ? `${item?.address_details?.property_number}, ${
                                  item?.address_details?.property_name
                                    ? item?.address_details?.property_name +
                                      ", "
                                    : ", "
                                } ${item?.address_details?.street_number}, ${
                                  item?.address_details?.town
                                }, ${item?.address_details?.country}-${
                                  item?.address_details?.postal_code
                                }`
                              : "NA"}
                          </td> */}
                              <td>
                                {item?.school_details
                                  ? `${item?.school_details?.name}, ${item?.school_details?.address}`
                                  : "NA"}
                              </td>
                              {companySettings?.membership && (
                                <td>
                                  {item?.trialAccountStatus === "1"
                                    ? "Trial"
                                    : item?.membership_details &&
                                      moment(
                                        item?.membership_details?.valid_till
                                      ).diff(moment()) < 0
                                    ? "Inactive"
                                    : item?.trialAccountStatus === "3"
                                    ? "Active"
                                    : "NA"}
                                </td>
                              )}

                              {/* <td>
                                <div className="d-flex justify-content-center action align-items-center">
                                  <button className="btn" title="View Student">
                                    <Link to={`/student/${item?.id}`}>
                                      <img
                                        src="assets/images/view.png"
                                        alt=""
                                      />
                                    </Link>
                                  </button>
                                  <button className="btn">
                                    <Link to={`/edit-student/${item?.id}`}>
                                      <img
                                        src="assets/images/edit.png"
                                        alt=""
                                      />
                                    </Link>
                                  </button>
                                  <button
                                    className="btn"
                                    onClick={() => {
                                      setDelId(item.id);
                                      handleShow();
                                    }}
                                    title="Delete Student"
                                  >
                                    <img
                                      src="assets/images/delete.png"
                                      alt=""
                                    />
                                  </button>
                                  <span
                                    className="px-1 cursor-pointer"
                                    title="Make Teacher"
                                    onClick={() => {
                                      navigate(
                                        `/student-to-teacher/${item?.id}`
                                      );
                                    }}
                                  >
                                    <i
                                      class="fa fa-exchange fa-xl theme-color"
                                      aria-hidden="true"
                                    ></i>
                                  </span>
                                </div>
                              </td> */}
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={10}>No data found.</td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {list?.length > 0 && (
            <Paginations
              handlePage={handlePage}
              page={page}
              total={total && total}
            />
          )}
        </div>
      </div>
      {/* {modalName === "filter" && (
        <FilterModal show={show} handleShow={handleShow} />
      )} */}
      {modalName === "delete" && (
        <DeleteModal
          page="Event"
          show={show}
          handleShow={handleShow}
          cancel={() => {
            setShow(false);
          }}
        />
      )}
      <ImportModal
        show={showImportModal}
        handleShow={() => setShowImportModal(false)}
        onImport={handleImportStudent}
        loading={importing}
      />
    </article>
  );
}

export default ImportExportStudent;
